// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import { PRICING_MODE } from '@/constants/pricing-mode';
import { Currency } from '@/currency/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import {
  BILLING_PLAN_INPUT_WIDTH,
  BillingPlanFormValues,
  TOGGLE_BUTTON_GROUP_SX,
  TOGGLE_BUTTON_SX,
} from '@/project/components/billing-plan-constants';
import {
  Box,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Decimal } from 'decimal.js';
import { Control, Controller } from 'react-hook-form';

interface Props {
  control: Control<BillingPlanFormValues>;
  pricingMode: BillingPlanFormValues['pricingMode'];
  price: string | null;
  quantity: number;
  chargeAmount: Decimal;
  currencyCode: IsoCurrencyCode;
  // ISO the currency selector is locked to (project currency or an invoiced-
  // charge freeze); undefined leaves the selector editable.
  lockTo?: IsoCurrencyCode;
  // Tooltip shown on the locked selector explaining why it cannot change.
  disabledTooltip?: string;
  // SKU catalog default for the current pricing mode, surfaced as a reference
  // beneath the input. Shown in its own currency, which may differ from the
  // project currency (the input is not prefilled across currencies).
  defaultPrice?: Currency | null;
}

export function BillingPlanPricingSection(props: Props) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        Pricing
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Controller
            control={props.control}
            name="pricingMode"
            render={(renderProps) => (
              <ToggleButtonGroup
                exclusive
                size="small"
                sx={TOGGLE_BUTTON_GROUP_SX}
                value={renderProps.field.value}
                onChange={(_event, value) => {
                  if (value !== null) {
                    renderProps.field.onChange(value);
                  }
                }}
              >
                <ToggleButton
                  value={PRICING_MODE.PER_UNIT}
                  sx={TOGGLE_BUTTON_SX}
                >
                  Per unit
                </ToggleButton>
                <ToggleButton value={PRICING_MODE.FIXED} sx={TOGGLE_BUTTON_SX}>
                  Fixed
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          />
          <Tooltip
            arrow
            title={
              <Box>
                <Typography variant="caption" fontWeight={600}>
                  Per unit:
                </Typography>
                <Typography variant="caption">
                  {' '}
                  price is multiplied by quantity (units x price).
                </Typography>
                <Box sx={{ mt: 1 }} />
                <Typography variant="caption" fontWeight={600}>
                  Fixed:
                </Typography>
                <Typography variant="caption">
                  {' '}
                  a flat price regardless of quantity.
                </Typography>
              </Box>
            }
          >
            <InfoOutlined
              sx={{
                fontSize: '1rem',
                color: 'text.secondary',
                cursor: 'help',
              }}
            />
          </Tooltip>
        </Box>
        <Box sx={{ width: BILLING_PLAN_INPUT_WIDTH, flexShrink: 0 }}>
          {/* One input bound to the shared `price` currency field. The
              meaning (per-unit rate vs flat amount) is derived from
              pricingMode on save — toggling the mode must not touch the
              input value so "99.99 as unit price" and "99.99 as fixed
              price" round-trip through the same slot without splitting the
              memory. The currency selector carries the line item's single
              currency for unit_price, fixed_price, and total_override. */}
          <FormCurrencyTextField
            control={props.control}
            name="price"
            placeholder="0.00"
            lockTo={props.lockTo}
            disabledTooltip={props.disabledTooltip}
          />
          {props.defaultPrice != null && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}
            >
              Default {formatCurrencyValue(props.defaultPrice)}
            </Typography>
          )}
        </Box>
      </Box>
      {props.pricingMode != null && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mt: 1,
            pl: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {props.pricingMode === PRICING_MODE.PER_UNIT
              ? `${props.quantity} units \u00D7 ${formatCurrencyValue({ amount: parseCurrencyAmount(props.price ?? '') ?? new CurrencyDecimal(0), isoCode: props.currencyCode })}/unit`
              : 'Fixed price'}
          </Typography>
          <Typography variant="caption" sx={{ pr: 1 }}>
            Charge{' '}
            <Box component="span" fontWeight={700}>
              {formatCurrencyValue({
                amount: props.chargeAmount,
                isoCode: props.currencyCode,
              })}
            </Box>
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
